import { FC, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AuthContext,
  CommonContext,
  useToggle,
  Wallet,
} from '@repo-level/core-front';

import { Button } from 'components/buttons';
import { mapCurrencyFlags, mapCurrencyFullName } from 'helpers/currency';
import { ModalsContext, ModalType } from 'contexts/modal';
import { useCurrentOptionWallet } from 'hooks/wallet';
import { formatWalletValue } from 'helpers/wallet';

import { ScCurrenciesBlock } from './styled';
import { ScCurrenciesItem, ScCurrenciesItems } from '../styled';
import { ImageComponent } from 'components/Image';

export const CurrenciesEnableBlock = () => {
  const { t } = useTranslation('deposits');

  const { wallets, deleteUserWallet, pendingWallets } = useContext(AuthContext);

  return (
    <ScCurrenciesBlock>
      <div className="title">{t('editCurrency.myCurrencies')}</div>
      <ScCurrenciesItems>
        {wallets.map((account) => (
          <CurrencyItem
            key={account.currency}
            disabled={wallets.length <= 1 || pendingWallets}
            onDelete={() => deleteUserWallet(account.currency)}
            {...account}
          />
        ))}
      </ScCurrenciesItems>
    </ScCurrenciesBlock>
  );
};

interface CurrencyItemProps extends Wallet {
  disabled: boolean;
  onDelete: () => void;
}

const CurrencyItem: FC<CurrencyItemProps> = ({
  currency,
  disabled,
  onDelete,
  amountCents,
}) => {
  const { t } = useTranslation();
  const { handleModal } = useContext(ModalsContext);
  const { currencies } = useContext(CommonContext);

  const walletValue = useMemo(
    () => formatWalletValue(currency, amountCents, currencies, true),
    [amountCents, currencies, currency]
  );

  const currentOptionWallet = useCurrentOptionWallet();

  const [pending, togglePending] = useToggle(false);

  const isCurrentCurrency = useMemo(
    () => currentOptionWallet.currency === currency,
    [currency, currentOptionWallet.currency]
  );

  const onClick = useCallback(() => {
    if (amountCents > 0)
      return handleModal(ModalType.CurrencyRemovalDeclined, {
        stayInSameWindow: true,
      })();
    onDelete();
    togglePending();
  }, [amountCents, handleModal, onDelete, togglePending]);

  const icon = useMemo(() => mapCurrencyFlags[currency], [currency]);

  return (
    <ScCurrenciesItem active={isCurrentCurrency}>
      <div className="content">
        {icon && <ImageComponent width={24} height={24} src={icon} alt="" />}
        <div>
          {walletValue} | {mapCurrencyFullName[currency]}
        </div>
      </div>

      <Button
        disabled={disabled}
        whiteTransparent
        medium
        autoPaddingHorizontal
        onClick={onClick}
        pending={pending}
      >
        {t('glossary:delete')}
      </Button>
    </ScCurrenciesItem>
  );
};
